import type { MembershipStatus } from '../services/invenioApi';

/**
 * Presentation rules for the membership renewal header bar.
 *
 * The server owns the dates and decides the phase; this module only decides how
 * that phase looks. Keeping the maths on one side and the styling on the other
 * means a member and an operator never disagree about whether a membership has
 * expired — the same `phase` drives the bar, the dashboard and the halt.
 *
 * The bar appears in the final week before expiry (amber), turns red on the
 * expiry date, and stays red once services are paused. It is deliberately not
 * dismissible: unlike the payments dot in `paymentAlertSeen.ts`, which is an
 * unread-notification, this is a live status that stops being true only when
 * the membership is renewed.
 */

export type MembershipBannerTone = 'warning' | 'critical';

export interface MembershipBanner {
  tone: MembershipBannerTone;
  /** The sentence shown to the member. */
  message: string;
  /** Label for the renewal call to action. */
  actionLabel: string;
  actionUrl: string;
  /** True once DOI generation is actually paused. */
  halted: boolean;
  className: string;
  iconClassName: string;
}

const TONE_STYLES: Record<MembershipBannerTone, { className: string; iconClassName: string }> = {
  warning: {
    className: 'bg-amber-50 text-amber-900 border-amber-200',
    iconClassName: 'text-amber-600',
  },
  critical: {
    className: 'bg-red-600 text-white border-red-700',
    iconClassName: 'text-white',
  },
};

/**
 * Decide whether to render the bar, and how.
 *
 * Returns null for every account that has nothing to renew: individual
 * researchers on pay-per-DOI (`enrolled: false`), and members whose expiry is
 * still more than a week away. A membership with no recorded end date is
 * perpetual and never warns.
 */
export function getMembershipBanner(
  status?: MembershipStatus | null
): MembershipBanner | null {
  if (!status?.enrolled) return null;
  if (!status.show_header_bar) return null;
  if (!status.validity_end) return null;

  const tone: MembershipBannerTone =
    status.phase === 'final_week' ? 'warning' : 'critical';
  const styles = TONE_STYLES[tone];

  return {
    tone,
    message: status.headline || fallbackMessage(status),
    actionLabel: status.blocked ? 'Renew to restore services' : 'Renew membership',
    actionUrl: status.renewal_url,
    halted: Boolean(status.blocked),
    className: styles.className,
    iconClassName: styles.iconClassName,
  };
}

/**
 * A message for the rare case where the server sent a phase but no headline —
 * an older API build, say. The bar must never render empty.
 */
function fallbackMessage(status: MembershipStatus): string {
  if (status.blocked) {
    return 'DOI generation is paused because your mVedra membership has expired. Renew to restore your services.';
  }
  if (status.phase === 'expired_grace') {
    const days = status.days_until_halt ?? 0;
    return `Your mVedra membership has expired. Renew within ${days} day${
      days === 1 ? '' : 's'
    } or DOI generation will be paused.`;
  }
  const days = status.days_until_expiry ?? 0;
  if (days === 0) {
    return 'Your mVedra membership expires today. Renew to avoid interruption.';
  }
  return `Your mVedra membership expires in ${days} day${
    days === 1 ? '' : 's'
  }. Renew to avoid interruption.`;
}

/**
 * Whether the dashboard should mark the membership as needing attention — the
 * same 30-day window the reminder emails use, so the dot and the first email
 * arrive together rather than a fortnight apart.
 */
export function membershipNeedsAttention(status?: MembershipStatus | null): boolean {
  if (!status?.enrolled) return false;
  return status.phase !== 'active';
}
